import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins } from '../../context/TCoinsContext';
import TCoinsDisplay from './TCoinsDisplay';
import DailyBonusModal from './DailyBonusModal';
import TCoinsPackages from './TCoinsPackages';
import CoinsExchange from './CoinsExchange';
import CoinsPurchase from './CoinsPurchase';
import TransactionHistory from './TransactionHistory';

const TCoinsWallet = ({ className = "", onClose }) => {
  const { currentUser } = useAuth();
  const { getExchangeValue, getTotalVirtual, getTotalReal } = useTCoins();
  const [activeTab, setActiveTab] = useState('wallet');
  const [showPackages, setShowPackages] = useState(false);
  const [selectedPackageData, setSelectedPackageData] = useState(null);
  const [showDailyBonusModal, setShowDailyBonusModal] = useState(false);

  const exchangeValue = getExchangeValue();
  const totalVirtual = getTotalVirtual();
  const totalReal = getTotalReal();

  const tabs = [
    { id: 'wallet', name: 'กระเป๋า', icon: '🪙' },
    { id: 'purchase', name: 'ซื้อเหรียญ', icon: '💰' },
    { id: 'bonus', name: 'โบนัส', icon: '🎁' },
    { id: 'exchange', name: 'แลกเปลี่ยน', icon: '💱' },
    { id: 'packages', name: 'แพ็กเกจ', icon: '🎈' },
    { id: 'history', name: 'ประวัติ', icon: '📊' }
  ];

  const handleExchange = async () => {
    if (!exchangeValue.canExchange) {
      alert(`ต้องมี T Coins แลกได้ขั้นต่ำ ${exchangeValue.minWithdrawal} T Coins`);
      return;
    }

    const confirmExchange = window.confirm(
      `ต้องการแลก ${exchangeValue.total} T Coins เป็น ${exchangeValue.net} บาท?\n\n` +
      `ค่าธรรมเนียม: ${exchangeValue.fee} บาท\n` +
      `ได้รับจริง: ${exchangeValue.net} บาท`
    );

    if (confirmExchange) {
      // Simulate exchange process
      alert('ระบบแลกเปลี่ยนกำลังพัฒนา กรุณาติดต่อแอดมิน');
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg ${className}`}>
      {/* Header - Mobile Optimized - Green theme to match marketplace */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-3 md:p-4 rounded-t-lg relative shadow-lg">
        {/* Close Button - Top Right Corner */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 md:top-3 md:right-3 text-white hover:text-gray-200 transition-colors p-1 z-10"
            title="ปิดหน้าจอ"
          >
            <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        <div className="flex items-center justify-between pr-8 md:pr-12">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-bold truncate">🏦 T Coins Wallet</h2>
            <p className="text-xs md:text-sm opacity-90 truncate">จัดการ T Coins ของคุณ</p>
          </div>
          <div className="text-right ml-2 flex-shrink-0">
            <div className="text-xs md:text-sm opacity-90 whitespace-nowrap">
              กระเป๋าเงินของคุณ
            </div>
            <div className="text-sm md:text-base font-bold flex items-center justify-end gap-1 md:gap-2 flex-wrap">
              <span>🎁{totalVirtual.toLocaleString()}</span>
              <span>💰{totalReal.toLocaleString()}</span>
              <span>฿{exchangeValue.net.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs - Mobile Optimized with Scroll - Show Full Names */}
      <div className="flex border-b overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 py-2 md:py-3 px-2 md:px-4 text-xs md:text-sm font-medium transition-colors whitespace-nowrap min-w-fit ${
              activeTab === tab.id
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <span className="mr-1 md:mr-2">{tab.icon}</span>
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-3 md:p-4">
        {activeTab === 'wallet' && (
          <div className="space-y-4">
            <TCoinsDisplay showExchangeValue={true} />
            
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <button
                onClick={() => {
                  setActiveTab('purchase');
                  setShowPackages(true);
                }}
                className="bg-green-500 text-white py-2 md:py-2.5 px-3 md:px-4 rounded-lg hover:bg-green-600 transition-colors text-sm md:text-base"
              >
                💰 ซื้อ T Coins
              </button>
              <button
                onClick={() => setActiveTab('exchange')}
                className="bg-blue-500 text-white py-2 md:py-2.5 px-3 md:px-4 rounded-lg hover:bg-blue-600 transition-colors text-sm md:text-base"
              >
                💱 แลกเงิน
              </button>
            </div>
          </div>
        )}

        {activeTab === 'purchase' && (
          <CoinsPurchase 
            selectedPackage={selectedPackageData}
            onPackageSelected={() => setSelectedPackageData(null)}
            onPackageSelectFromModal={(packageData) => {
              setSelectedPackageData(packageData);
              setShowPackages(false);
            }}
          />
        )}

        {activeTab === 'bonus' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-green-500 to-yellow-400 rounded-lg p-6 text-white text-center">
              <div className="text-4xl mb-3">🎁</div>
              <h3 className="text-xl font-bold mb-2">Daily Login Bonus</h3>
              <p className="text-sm opacity-90 mb-4">
                เข้าใช้ทุกวันเพื่อรับโบนัสและเพิ่ม Streak!
              </p>
              <button
                onClick={() => setShowDailyBonusModal(true)}
                className="bg-white text-green-600 font-semibold py-2 px-6 rounded-lg hover:bg-gray-100 transition-colors"
              >
                เปิด Daily Bonus
              </button>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">💡 ข้อมูลโบนัส</h4>
              <div className="text-xs text-blue-700 space-y-1">
                <div>• วันแรก: 10 🎁</div>
                <div>• เพิ่ม 5 🎁 ทุกวัน</div>
                <div>• วันที่ 7: 50 🎁 (Milestone!)</div>
                <div>• วันที่ 14: 100 🎁 (Milestone!)</div>
                <div>• วันที่ 30: 200 🎁 (Milestone!)</div>
                <div className="mt-2 font-semibold text-red-600">⚠️ ถ้าข้ามวัน Streak จะรีเซ็ต!</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'exchange' && (
          <CoinsExchange />
        )}

        {activeTab === 'packages' && (
          <TCoinsPackages 
            onClose={() => setShowPackages(false)}
            onSelectPackage={(packageData) => {
              setActiveTab('purchase');
              // ส่งข้อมูลแพ็คเกจไปยัง CoinsPurchase
              setSelectedPackageData(packageData);
            }}
          />
        )}

        {activeTab === 'history' && (
          <TransactionHistory />
        )}
      </div>

      {/* Packages Modal - Mobile Optimized */}
      {showPackages && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 md:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPackages(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Top Right Corner */}
            <button
              onClick={() => setShowPackages(false)}
              className="absolute top-2 right-2 md:top-3 md:right-3 text-gray-500 hover:text-gray-700 transition-colors p-1 z-10 bg-white rounded-full shadow-md"
              title="ปิด"
            >
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <TCoinsPackages 
              onClose={() => setShowPackages(false)}
              onSelectPackage={(packageData) => {
                if (activeTab === 'purchase') {
                  // If in purchase tab, set the package data
                  setSelectedPackageData(packageData);
                  setShowPackages(false);
                } else {
                  // If not in purchase tab, switch to purchase tab and set package
                  setActiveTab('purchase');
                  setSelectedPackageData(packageData);
                  setShowPackages(false);
                }
              }}
            />
          </div>
        </div>
      )}
      
      {/* Daily Bonus Modal */}
      <DailyBonusModal
        isOpen={showDailyBonusModal}
        onClose={() => setShowDailyBonusModal(false)}
      />
    </div>
  );
};

export default TCoinsWallet;
